/**
 * @fileoverview The org Dashboard's Students tab: every student the
 * organization can see, one row each, the way a program's own Students tab
 * lists its roster — name, where they learn and with whom, and when they last
 * had a lesson.
 *
 * The population is the Students figure's own (`GET /org/students`, the same
 * org-attributed rule every org count uses), so the tile and the list agree.
 * The list opens on active students only, because the tile counts only them.
 * A student is inactive when their record is archived or when every class
 * they were on has been archived or deleted (or every program they were in has
 * finished); "Show inactive" adds them back, each marked, for "who did we
 * teach this year". Never a contact field: an org sees who is on its rosters,
 * not how to reach a family. Consent is deliberately not listed here (a product
 * decision, Sept 2026): this tab answers "who do we teach", and a class roster
 * or an instructor's page is where a family's answer is read.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import orgApi from "@/lib/orgApi";
import LessonFilterBar from "@/components/studio/LessonFilterBar";
import { Card, Icon, ListRow, Tag } from "@/components/studio/primitives";
import { cn } from "@/lib/utils";
import { dayLabel } from "@/lib/orgReporting";
import { distinctOptions } from "@/lib/orgSessions";

const note = "px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]";

/** Active per the server; a row from before `active` existed reads by its archive flag. */
const isActive = (s) => (s.active === undefined ? !s.archived : s.active);

/**
 * @param {{ filter: string|null, onFilter: (filter: string|null) => void }} props
 */
export default function OrgStudentsTab({ filter, onFilter }) {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [bar, setBar] = useState({ program: null, instructor: null, search: "" });

  useEffect(() => {
    let live = true;
    orgApi
      .get("/org/students")
      .then((res) => live && setPayload(res.data))
      .catch(() => live && setPayload({ students: [], failed: true }));
    return () => {
      live = false;
    };
  }, []);

  const all = useMemo(() => payload?.students || [], [payload]);
  /* In the URL like every dashboard filter. "archived" is the filter's old
     name, from when only archived records were hidden; a saved link keeps
     working. */
  const showInactive = filter === "inactive" || filter === "archived";
  const active = useMemo(() => all.filter(isActive), [all]);
  const inactiveCount = all.length - active.length;

  const programs = useMemo(() => {
    const out = new Map();
    for (const s of all) for (const p of s.programs || []) if (!out.has(p.program_id)) out.set(p.program_id, p.title);
    return [...out.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);
  const instructors = useMemo(() => distinctOptions(all, (s) => s.instructor_id, (s) => s.instructor_name), [all]);

  const shown = useMemo(() => {
    const q = (bar.search || "").trim().toLowerCase();
    return (showInactive ? all : active).filter(
      (s) =>
        (!bar.program || (s.programs || []).some((p) => p.program_id === bar.program)) &&
        (!bar.instructor || s.instructor_id === bar.instructor) &&
        (!q || `${s.name || ""} ${s.instructor_name || ""}`.toLowerCase().includes(q)),
    );
  }, [active, all, showInactive, bar]);

  return (
    <>
      <LessonFilterBar
        value={bar}
        onChange={(next) => setBar({ program: next.program, instructor: next.instructor, search: next.search })}
        periods={false}
        statuses={false}
        programs={programs}
        instructors={instructors}
        searchPlaceholder="Search students"
      >
        <button
          type="button"
          aria-pressed={showInactive}
          onClick={() => onFilter(showInactive ? null : "inactive")}
          data-testid="dashboard-student-inactive-toggle"
          className={cn(
            "inline-flex h-9 items-center gap-2 rounded-full border px-4 font-sans text-[13px] transition-colors",
            showInactive
              ? "border-[var(--accent-yellow-border-strong)] bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]"
              : "border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)] hover:bg-[var(--studio-interactive)]",
          )}
        >
          <Icon name={showInactive ? "eye" : "eyeOff"} size={14} />
          {showInactive ? "Hide inactive" : "Show inactive"}
          {payload && !payload.failed && (
            <span className="font-mono text-[12px] tabular-nums text-[var(--text-tertiary)]">{inactiveCount}</span>
          )}
        </button>
      </LessonFilterBar>
      <Card padding={0} className="mb-3" data-testid="dashboard-students">
        {!payload ? (
          <div className={note}>Loading…</div>
        ) : payload.failed ? (
          <div className={note}>Couldn&rsquo;t load the students.</div>
        ) : shown.length === 0 ? (
          <div className={note}>
            {all.length === 0
              ? "No students on your programs' rosters yet."
              : !showInactive && active.length === 0
                ? "No active students. Show inactive to see everyone who has been on a roster."
                : "Nothing matches these filters."}
          </div>
        ) : (
          shown.map((s, i) => {
            const firstClass = (s.classes || [])[0];
            const open = firstClass
              ? () => navigate(`/org/classes/${firstClass.class_id}`)
              : s.instructor_id
                ? () => navigate(`/org/instructors/${s.instructor_id}`)
                : undefined;
            return (
              <ListRow
                key={s.student_id}
                borderBottom={i < shown.length - 1}
                data-testid={`dashboard-student-${s.student_id}`}
                title={s.name}
                subtitle={[
                  (s.programs || []).map((p) => p.title).join(", ") || null,
                  (s.classes || []).map((c) => (c.archived ? `${c.name} (archived)` : c.name)).join(", ") || null,
                  s.solo ? "Solo lessons" : null,
                  s.instructor_name,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                meta={s.last_lesson ? `Last lesson ${dayLabel(s.last_lesson)}` : undefined}
                trailing={
                  s.imported || !isActive(s) ? (
                    <span className="flex items-center gap-2">
                      {s.imported && <Tag>Imported · needs review</Tag>}
                      {s.archived ? <Tag>Archived</Tag> : !isActive(s) && <Tag>Inactive</Tag>}
                    </span>
                  ) : undefined
                }
                onPress={open}
              />
            );
          })
        )}
      </Card>
      <p className="max-w-3xl font-sans text-[12px] leading-relaxed text-[var(--text-tertiary)]">
        Everyone on your programs&rsquo; rosters and scheduled lessons. A student an instructor teaches privately is
        not listed. Names only: contact details stay with the instructor and the family.
      </p>
    </>
  );
}
